import { Pic } from "@/components/site/Pic";

import { ArrowUpRight, Box, Droplets, Leaf, Package, Wind } from "lucide-react";
import { Nav } from "@/components/site/Nav";
import { Footer } from "@/components/site/Footer";
import { TrustBand } from "@/components/site/TrustBand";
import { RelatedProducts } from "@/components/site/RelatedProducts";
import { Contact } from "@/components/site/Contact";
import { Reveal } from "@/components/site/Motion";
import { PRODUCT_DETAILS } from "@/data/productDetails";
import { scrollToId } from "@/lib/scroll";
import { usePageMeta, useJsonLd } from "@/lib/pageMeta";
import { useT } from "@/i18n";

const FEATURES = [
  ["species", Leaf, "Pino e abete", "Trucioli di sole conifere, provenienti dalla lavorazione del nostro legno."],
  ["dry", Droplets, "Essiccati", "Il legno essiccato assorbe l'umidità e mantiene il giaciglio asciutto."],
  ["dust", Wind, "Depolverati", "La depolverazione riduce la polvere, a tutela delle vie respiratorie di animali e operatori."],
  ["safe", Box, "Senza sostanze tossiche", "Materiale naturale, senza additivi chimici."],
];
const USES = [["stalls", "Stalle e allevamenti"], ["horses", "Equini e maneggi"], ["cattle", "Bovini"], ["rabbits", "Conigli e piccoli animali"]];
const FORMATS = [
  ["bale", "Balla compressa", [["size", "Dimensioni", "40 × 40 × 80 cm"], ["weight", "Peso", "16 – 20 kg"]]],
  ["pallet", "Bancale", [["qty", "Composizione", "24 o 28 balle"], ["other", "Altri formati", "Su richiesta"]]],
];

const askQuote = (t) => {
  window.dispatchEvent(new CustomEvent("prefill-quote", { detail: { project_type: "Trucioli depolverati", message: t("lett.msg", "Vorrei un preventivo per trucioli depolverati per lettiere: quantità (balle o bancali), luogo di consegna e frequenza di fornitura.") } }));
  scrollToId("#contatti");
};

const Hero = ({ t }) => (
  <header className="relative overflow-hidden bg-[#121110] px-5 pb-20 pt-40 text-[#F7F5F0] sm:px-8 lg:pb-28" data-testid="lett-hero">
    <Pic src="/img/emma/p-trucioli-lettiere.jpg" alt={t("lett.heroAlt", "Trucioli di pino e abete depolverati per lettiere EMMA INDUSTRIE")} className="absolute inset-0 h-full w-full object-cover opacity-40" />
    <div className="photo-fade absolute inset-0 bg-gradient-to-r from-[#121110] via-[#121110]/80 to-transparent" />
    <div className="relative mx-auto max-w-[1500px]">
      <p className="font-mono text-xs uppercase tracking-[0.25em] text-[#D98A52]">{t("lett.label", "Trucioli depolverati · Lettiere per animali")}</p>
      <h1 className="mt-6 max-w-4xl font-display text-4xl font-bold leading-[1.02] tracking-tight sm:text-5xl lg:text-6xl">{t("lett.h1", "Lettiere in trucioli di legno, naturali e depolverate.")}</h1>
      <p className="mt-6 max-w-2xl text-base leading-relaxed text-[#E4DED3] sm:text-lg">{t("lett.p", "Trucioli di pino e abete essiccati e depolverati, in balle compresse: un giaciglio asciutto, morbido e pulito per stalle, equini, bovini e conigli.")}</p>
      <button onClick={() => askQuote(t)} className="btn-amber mt-10" data-testid="lett-hero-quote">{t("lett.cta", "Richiedi un preventivo")} <ArrowUpRight className="h-4 w-4" /></button>
    </div>
  </header>
);

const Features = ({ t }) => (
  <section className="mx-auto max-w-[1500px]" data-testid="lett-features">
    <h2 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">{t("lett.featH", "Perché i nostri trucioli")}</h2>
    <div className="mt-10 grid gap-px bg-[#121110]/10 sm:grid-cols-2 lg:grid-cols-4">
      {FEATURES.map(([k, Icon, h, p]) => (
        <Reveal key={k} className="bg-[#F7F5F0] p-6" data-testid={`lett-feature-${k}`}>
          <Icon className="h-6 w-6 text-[#C26D38]" />
          <h3 className="mt-4 font-display text-xl font-bold">{t(`lett.f.${k}H`, h)}</h3>
          <p className="mt-2 text-[15px] leading-relaxed text-[#3D3833]">{t(`lett.f.${k}`, p)}</p>
        </Reveal>
      ))}
    </div>
  </section>
);

const Uses = ({ t }) => (
  <section className="mx-auto mt-24 grid max-w-[1500px] gap-10 lg:grid-cols-12" data-testid="lett-uses">
    <div className="lg:col-span-5">
      <h2 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">{t("lett.usesH", "Impieghi")}</h2>
      <p className="mt-4 text-base leading-relaxed text-[#3D3833]">{t("lett.usesP", "Adatti ad allevamenti, maneggi e piccole stalle: la lettiera si stende facilmente e si rinnova con poco materiale.")}</p>
      <ul className="mt-8 border-b border-[#121110]/15">
        {USES.map(([k, l]) => <li key={k} className="border-t border-[#121110]/15 py-4 font-display text-xl font-bold" data-testid={`lett-use-${k}`}>{t(`lett.u.${k}`, l)}</li>)}
      </ul>
    </div>
    <div className="aspect-[4/3] overflow-hidden bg-[#E4DED3] lg:col-span-7">
      <Pic src="/img/emma/p-trucioli.jpg" alt={t("lett.usesAlt", "Trucioli di legno per lettiere")} loading="lazy" className="h-full w-full object-cover" />
    </div>
  </section>
);

const Formats = ({ t }) => (
  <section className="mx-auto mt-24 max-w-[1500px]" data-testid="lett-formats">
    <h2 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">{t("lett.formH", "Formati di fornitura")}</h2>
    <div className="mt-10 grid gap-6 md:grid-cols-2">
      {FORMATS.map(([k, h, rows]) => (
        <div key={k} className="bg-[#121110] p-6 text-[#F7F5F0] sm:p-8" data-testid={`lett-format-${k}`}>
          <Package className="h-6 w-6 text-[#D98A52]" />
          <h3 className="mt-4 font-display text-2xl font-bold">{t(`lett.fm.${k}`, h)}</h3>
          <dl className="mt-6 divide-y divide-white/10 border-y border-white/10">
            {rows.map(([rk, l, v]) => (
              <div key={rk} className="flex items-baseline justify-between gap-4 py-3">
                <dt className="font-mono text-xs uppercase tracking-[0.18em] text-[#D98A52]">{t(`lett.fm.${k}.${rk}L`, l)}</dt>
                <dd className="text-right font-display text-lg font-bold" data-testid={`lett-format-${k}-${rk}`}>{t(`lett.fm.${k}.${rk}`, v)}</dd>
              </div>
            ))}
          </dl>
        </div>
      ))}
    </div>
    <button onClick={() => askQuote(t)} className="btn-amber mt-10" data-testid="lett-formats-quote">{t("lett.cta", "Richiedi un preventivo")} <ArrowUpRight className="h-4 w-4" /></button>
  </section>
);

const Care = ({ t }) => {
  const a = PRODUCT_DETAILS.nido.article;
  return (
    <section className="mx-auto mt-24 max-w-[1500px]" data-testid="lett-care">
      <h2 className="max-w-3xl font-display text-3xl font-bold tracking-tight sm:text-4xl">{t("art.nido.title", a.title)}</h2>
      <p className="mt-6 max-w-3xl text-base leading-relaxed text-[#3D3833] sm:text-lg">{t("art.nido.intro", a.intro)}</p>
      <div className="mt-10 grid gap-8 sm:grid-cols-2">
        {a.blocks.map(([k, h, p]) => (
          <div key={k}>
            <h3 className="font-display text-xl font-bold">{t(`art.nido.${k}H`, h)}</h3>
            <p className="mt-2 text-[15px] leading-relaxed text-[#3D3833]">{t(`art.nido.${k}`, p)}</p>
          </div>
        ))}
      </div>
    </section>
  );
};

export default function LettiereAnimali() {
  const t = useT();
  usePageMeta({
    title: t("lett.metaTitle", "Trucioli depolverati per lettiere animali"),
    desc: t("lett.metaDesc", "Trucioli di pino e abete essiccati e depolverati per lettiere di stalle, equini, bovini e conigli. Balle compresse 40×40×80 cm da 16–20 kg, bancali da 24 o 28 balle. Richiedi un preventivo."),
    path: "/lettiere-animali",
    image: "/img/emma/p-trucioli-lettiere.jpg",
  });
  useJsonLd("lettiere", {
    "@context": "https://schema.org",
    "@type": "Product",
    name: t("lett.h1", "Lettiere in trucioli di legno, naturali e depolverate."),
    description: t("lett.p", "Trucioli di pino e abete essiccati e depolverati, in balle compresse: un giaciglio asciutto, morbido e pulito per stalle, equini, bovini e conigli."),
    image: ["p-trucioli-lettiere", "p-trucioli"].map((n) => `${window.location.origin}/img/emma/${n}.jpg`),
    category: "Lettiere per animali",
    material: "Pino e abete",
    size: "40 × 40 × 80 cm",
    weight: { "@type": "QuantitativeValue", minValue: 16, maxValue: 20, unitCode: "KGM" },
    brand: { "@type": "Brand", name: "EMMA INDUSTRIE" },
    manufacturer: { "@type": "Organization", name: "EMMA INDUSTRIE" },
  });
  return (
    <div className="bg-[#F7F5F0] text-[#121110]" data-testid="lett-page">
      <Nav />
      <Hero t={t} />
      <main className="px-5 py-24 sm:px-8 lg:py-32">
        <Features t={t} />
        <Uses t={t} />
        <Formats t={t} />
        <Care t={t} />
      </main>
      <TrustBand />
      <RelatedProducts />
      <Contact />
      <Footer />
    </div>
  );
}
